import React, {useCallback, useEffect, useState} from 'react';
import {Link} from '@mui/material';
import type {Media} from 'api/types';
import {isFeatureEnabled} from 'configuration';
import {getMediaUrl} from 'api';
import {useGetTokens} from 'services/auth';
import {
  useDownloadFile,
  useGetManifestationEditValue,
  usePreviewFile,
} from 'services/metadata';
import {useHandleDelayedCall} from 'services/utils';
import {Layout, ToolBar} from 'components';
import {ConfirmButton} from 'components/button/ConfirmButton';
import {useDeleteMedia} from '../hooks/useDeleteMedia';

type Props = {
  file: Media;
  manifestationId: string;
  mock?: boolean;
  refreshMedia: () => void;
};

export const FileToolbar: React.FC<Props> = ({
  file,
  manifestationId,
  mock = false,
  refreshMedia,
}) => {
  const getEditValue = useGetManifestationEditValue();
  const eanValue = getEditValue('ean', manifestationId);
  const ean = typeof eanValue === 'string' ? eanValue : undefined;

  const [directDownloadUrl, setDirectDownloadUrl] = useState<
    string | undefined
  >();
  const getTokens = useGetTokens();
  const {preview} = usePreviewFile(file, mock);
  const {download} = useDownloadFile(file, mock);
  const deleteMedia = useDeleteMedia();

  const handlePreviewDelayed = useHandleDelayedCall(preview);
  const handleDownloadDelayed = useHandleDelayedCall(download);

  const handleDelete = useCallback(
    (subType: Media['subType'], fileNr: Media['fileNr']) => {
      return new Promise<void>(() => {
        if (!ean) {
          throw new Error('EAN is required to delete media');
        }
        deleteMedia(ean, subType, fileNr).then(() => {
          refreshMedia();
        });
      });
    },
    [deleteMedia, ean, refreshMedia],
  );

  const canDelete =
    ean &&
    file.type === 'TRYKKUNDERLAG' &&
    !file.compressed &&
    isFeatureEnabled('deleteMedia');

  /**
   * If above threshold, use direct download.
   * Some users struggle with big files. Maybe because of blob size, but we
   * just offer a direct download option.
   */
  useEffect(() => {
    const fileThreshold = 200 * (1024 * 1024); // 200 MB

    if ((file.size ?? 0) > fileThreshold) {
      getMediaUrl(file.id, getTokens, mock).then(url =>
        setDirectDownloadUrl(url),
      );
    }
  }, [file.id, file.size, getTokens, mock]);

  return (
    <Layout horizontal adjustCenter adjustRight sx={{width: '100%'}}>
      <ToolBar spacing="tight">
        {directDownloadUrl ? (
          <Link
            download={'download'}
            target="_blank"
            rel="noopener noreferrer"
            href={directDownloadUrl}
            underline="hover">
            Direktelink
          </Link>
        ) : null}

        {canDelete ? (
          <>
            <ConfirmButton
              onConfirm={() => handleDelete(file.subType, file.fileNr)}
              icon={'Delete'}
              iconOnly
              title={''}
            />
          </>
        ) : null}

        <ToolBar.ControlledIconTool
          icon="Download"
          size="small"
          onClick={handleDownloadDelayed.handleCall}
          isCalling={handleDownloadDelayed.isCalling}
          isDelayed={handleDownloadDelayed.isDelayed}
        />
        <ToolBar.ControlledIconTool
          icon="Preview"
          size="small"
          onClick={handlePreviewDelayed.handleCall}
          isCalling={handlePreviewDelayed.isCalling}
          isDelayed={handlePreviewDelayed.isDelayed}
        />
      </ToolBar>
    </Layout>
  );
};
